"use strict";
/*
This file is part of web3.js.

web3.js is free software: you can redistribute it and/or modify
it under the terms of the GNU Lesser General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

web3.js is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Lesser General Public License for more details.

You should have received a copy of the GNU Lesser General Public License
along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.QRNS = void 0;
const web3_core_1 = require("@theqrl/web3-core");
const web3_errors_1 = require("@theqrl/web3-errors");
const web3_qrl_1 = require("@theqrl/web3-qrl");
const web3_net_1 = require("@theqrl/web3-net");
const web3_types_1 = require("@theqrl/web3-types");
const config_js_1 = require("./config.js");
const registry_js_1 = require("./registry.js");
const resolver_js_1 = require("./resolver.js");
/**
 * This class is designed to interact with the QRNS system on the QRL blockchain.
 *
 */
class QRNS extends web3_core_1.Web3Context {
    /**
     * Use to create an instance of QRNS
     * @param registryAddr - (Optional) The address of the QRNS registry (default: mainnet registry address)
     * @param provider - (Optional) The provider to use for the QRNS instance
     * @example
     * ```ts
     * const qrns = new QRNS(
     * 	"Q00000000000C2E074eC69A0dFb2997BA6C7d2e1e",
     * 	"http://localhost:8545"
     * );
     *
     * console.log(qrns.defaultChain);
     * > mainnet
     * ```
     */
    constructor(registryAddr, provider) {
        super(provider !== null && provider !== void 0 ? provider : '');
        this.registryAddress = registryAddr !== null && registryAddr !== void 0 ? registryAddr : config_js_1.registryAddresses.main; // will default to main registry address
        this._registry = new registry_js_1.Registry(this.getContextObject(), registryAddr);
        this._resolver = new resolver_js_1.Resolver(this._registry);
    }
    /**
     * Returns the Resolver by the given address
     * @param name - The name of the QRNS domain
     * @returns - An contract instance of the resolver
     *
     * @example
     * ```ts
     * const resolver = await qrns.getResolver('resolver');
     *
     * console.log(resolver.options.address);
     * > 'Q1234567890123456789012345678901234567890'
     * ```
     */
    getResolver(name) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._registry.getResolver(name);
        });
    }
    /**
     * Returns true if the record exists
     * @param name - The QRNS name
     * @returns - Returns `true` if node exists in this QRNS registry. This will return `false` for records that are in the legacy QRNS registry but have not yet been migrated to the new one.
     * @example
     * ```ts
     * const exists = await web3.qrl.qrns.recordExists('qrl.qrns');
     * ```
     */
    recordExists(name) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._registry.recordExists(name);
        });
    }
    /**
     * Returns the caching TTL (time-to-live) of a QRNS name.
     * @param name - The QRNS name
     * @returns - Returns the caching TTL (time-to-live) of a name.
     * @example
     * ```ts
     * const owner = await web3.qrl.qrns.getTTL('qrl.qrns');
     * ```
     */
    getTTL(name) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._registry.getTTL(name);
        });
    }
    /**
     * Returns the owner by the given name and current configured or detected Registry
     * @param name - The QRNS name
     * @returns - Returns the address of the owner of the name.
     * @example
     * ```ts
     * const owner = await web3.qrl.qrns.getOwner('qrl.qrns');
     * ```
     */
    getOwner(name) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._registry.getOwner(name);
        });
    }
    /**
     * Resolves a QRNS name to a QRL address.
     * @param QRNSName - The QRNS name to resolve
     * @param coinType - (Optional) The coin type, defaults to 60 (QRL)
     * @returns - The QRL address of the given name
     * ```ts
     * const address = await web3.qrl.qrns.getAddress('qrl.qrns');
     * console.log(address);
     * > 'QfB6916095ca1df60bB79Ce92cE3Ea74c37c5d359'
     * ```
     */
    getAddress(QRNSName_1) {
        return __awaiter(this, arguments, void 0, function* (QRNSName, coinType = 60) {
            return this._resolver.getAddress(QRNSName, coinType);
        });
    }
    /**
     * Returns the X and Y coordinates of the curve point for the public key.
     * @param QRNSName - The QRNS name
     * @returns - The X and Y coordinates of the curve point for the public key
     * @example
     * ```ts
     * const key = await web3.qrl.qrns.getPubkey('qrl.qrns');
     * console.log(key);
     * > {
     * "0": "0x0000000000000000000000000000000000000000000000000000000000000000",
     * "1": "0x0000000000000000000000000000000000000000000000000000000000000000",
     * "x": "0x0000000000000000000000000000000000000000000000000000000000000000",
     * "y": "0x0000000000000000000000000000000000000000000000000000000000000000"
     * }
     * ```
     */
    getPubkey(QRNSName) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._resolver.getPubkey(QRNSName);
        });
    }
    /**
     * Returns the content hash object associated with a QRNS node.
     * @param QRNSName - The QRNS name
     * @returns - The content hash object associated with a QRNS node
     * @example
     * ```ts
     * const hash = await web3.qrl.qrns.getContenthash('qrl.qrns');
     * console.log(hash);
     * > 'QmaEBknbGT4bTQiQoe2VNgBJbRfygQGktnaW5TbuKixjYL'
     * ```
     */
    getContenthash(QRNSName) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._resolver.getContenthash(QRNSName);
        });
    }
    /**
     * Checks if the current used network is synced and looks for QRNS support there.
     * Throws an error if not.
     * @returns - The address of the QRNS registry if the network has been detected successfully
     * @example
     * ```ts
     * console.log(await web3.qrl.qrns.checkNetwork());
     * > 'Q00000000000C2E074eC69A0dFb2997BA6C7d2e1e'
     * ```
     */
    checkNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            const now = Date.now() / 1000;
            if (!this._lastSyncCheck || now - this._lastSyncCheck > 3600) {
                const syncInfo = yield (0, web3_qrl_1.isSyncing)(this);
                if (!(typeof syncInfo === 'boolean' && !syncInfo)) {
                    throw new web3_errors_1.QRNSNetworkNotSyncedError();
                }
                this._lastSyncCheck = now;
            }
            if (this._detectedAddress) {
                return this._detectedAddress;
            }
            const networkType = yield (0, web3_net_1.getId)(this, Object.assign(Object.assign({}, web3_types_1.DEFAULT_RETURN_FORMAT), { number: web3_types_1.FMT_NUMBER.HEX })); // get the network from provider
            const addr = config_js_1.registryAddresses[config_js_1.networkIds[networkType]];
            if (typeof addr === 'undefined') {
                throw new web3_errors_1.QRNSUnsupportedNetworkError(networkType);
            }
            this._detectedAddress = addr;
            return this._detectedAddress;
        });
    }
    /**
     * Returns true if the related Resolver does support the given signature or interfaceId.
     * @param QRNSName - The QRNS name
     * @param interfaceId - The signature of the function or the interfaceId as described in the QRNS documentation
     * @returns - `true` if the related Resolver does support the given signature or interfaceId.
     * @example
     * ```ts
     * const supports = await web3.qrl.qrns.supportsInterface('qrl.qrns', 'addr(bytes32');
     * console.log(supports);
     * > true
     * ```
     */
    supportsInterface(QRNSName, interfaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._resolver.supportsInterface(QRNSName, interfaceId);
        });
    }
    /**
     * @returns - Returns all events that can be emitted by the QRNS registry.
     */
    get events() {
        return this._registry.events;
    }
}
exports.QRNS = QRNS;
//# sourceMappingURL=qrns.js.map